'use client';
import { signIn } from '@lib/auth';
import { useMicrocopy } from '@lib/cms/microcopy/client';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import { failingFetcher } from '@lib/http/fetch';
import {
  GetProductListsV2Response,
  LookupProductListsResponse,
  ProductListV2,
} from '@lib/types/api/ProductListV2';
import { debounce } from '@lib/util/debounce';
import { useSession } from 'next-auth/react';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSWR from 'swr';
import {
  ChangeFavoriteListModal,
  FavoriteListModal,
  SnackBar,
  type ChangeFavoriteListModalPropsMicrocopy,
  type FavoriteListModalFormData,
  type FavoriteListModalPropsMicrocopy,
  type ListData,
} from 'ui-components';

const DEFAULT_NAMESPACE = 'assetstore';
const DEFAULT_SOURCE_SYSTEM = 'genesis';

const createListFetcher = async (
  name: string,
  visibility: string
): Promise<ProductListV2> => {
  const payload = {
    name,
    visibility,
  };
  const response = await fetch('/api/lists', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('Failed to create list');
  }

  return response.json();
};

const addItemToList = async (
  listId: string,
  productId: string
): Promise<void> => {
  const response = await fetch(`/api/lists/${listId}/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      namespace: DEFAULT_NAMESPACE,
      productId,
      sourceSystem: DEFAULT_SOURCE_SYSTEM,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to add item to list');
  }
};

const removeItemFromList = async (
  listId: string,
  productId: string
): Promise<void> => {
  const response = await fetch(
    `/api/lists/${listId}/items/${DEFAULT_NAMESPACE}/${productId}`,
    { method: 'DELETE' }
  );

  if (!response.ok && response.status !== 204) {
    throw new Error('Failed to remove item from list');
  }
};

const setFavoriteList = async (listId: string): Promise<void> => {
  const response = await fetch(`/api/lists/${listId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isFavorites: true }),
  });

  if (!response.ok) {
    throw new Error('Failed to set favorite list');
  }
};

const toListData = (list: ProductListV2): ListData => ({
  id: list.id,
  name: list.name,
  count: list.itemCount,
  type: list.visibility,
  personal: true,
  default: list.isFavorites,
});

type ModalFlowState =
  | { step: 'idle' }
  | {
      step: 'create-list';
      productId: string;
      intent: 'add' | 'remove';
      allowDefaultPromotion?: boolean;
    }
  | {
      step: 'change-list';
      productId: string;
      initialSelectedListIds: string[];
      fromCreateFlow?: boolean;
      intent: 'add' | 'remove';
      allowDefaultPromotion?: boolean;
    };

type ProductListsContextValue = {
  lists: ProductListV2[];
  defaultList: ProductListV2 | null;
  isLoading: boolean;
  isProductInList: (productId: string) => boolean | null;
  toggleProductList: (productId: string) => void;
  onNewList: () => void;
  onChangeList: (productId: string) => void;
  fetchProductMembership: (productId: string) => Promise<string[]>;
  fetchBatchProductMembership: (productIds: string[]) => Promise<void>;
};

const ProductListsContext = createContext<ProductListsContextValue | null>(
  null
);

const DEFAULT_CONTEXT: ProductListsContextValue = {
  lists: [],
  defaultList: null,
  isLoading: false,
  isProductInList: () => null,
  toggleProductList: () => {},
  onNewList: () => {},
  onChangeList: () => {},
  fetchProductMembership: () => Promise.resolve([]),
  fetchBatchProductMembership: () => Promise.resolve(),
};

export const ProductListsProvider = ({ children }: PropsWithChildren) => {
  const { flags } = useFlags();
  const isProductListsEnabled =
    flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;
  const { status, data: session } = useSession();
  const isAuthenticated = status === 'authenticated';
  const shouldFetch = isAuthenticated && isProductListsEnabled;

  const [snackbar, setSnackbar] = useState<{
    type: 'added' | 'removed';
    listName: string;
    listSlug: string;
    productId: string;
    key: number;
  } | null>(null);

  const [modalFlow, setModalFlow] = useState<ModalFlowState>({
    step: 'idle',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [optimisticFavoriteId, setOptimisticFavoriteId] = useState<
    string | null
  >(null);

  const [productMembership, setProductMembership] = useState<
    Record<string, string[]>
  >({});

  const mc = useMicrocopy('product-lists', {
    'product-lists.added-to': 'Added to',
    'product-lists.removed-from': 'Removed from',
    'product-lists.view-list': 'View list',
    'product-lists.change-list': 'Change list',
    'product-lists.add-to-new-list': 'Add to new list',
    'product-lists.close': 'Close',
    // FavoriteListModal microcopy
    'product-lists.create-modal-title': 'New list',
    'product-lists.create-modal-subtitle': 'Create a new list',
    'product-lists.create-modal-name-label': 'New list name',
    'product-lists.create-modal-name-placeholder':
      'eg. Category or Project name',
    'product-lists.create-modal-visibility-label': 'Visibility',
    'product-lists.create-modal-cta': 'CREATE AND ADD TO',
    'product-lists.create-modal-close': 'Close modal',
    'product-lists.create-modal-clear-input': 'Clear input',
    'product-lists.visibility-group-label': 'Content Visibility',
    'product-lists.visibility-private': 'Private',
    'product-lists.visibility-private-help': 'Just for you',
    'product-lists.visibility-public': 'Public',
    'product-lists.visibility-public-help': 'Anyone can view',
    // ChangeFavoriteListModal microcopy
    'product-lists.change-modal-title': 'Change list',
    'product-lists.change-modal-subtitle': 'Add to existing or a new list',
    'product-lists.change-modal-title-from-create': 'Add to list',
    'product-lists.change-modal-subtitle-from-create':
      'Add to a new or change to an existing list',
    'product-lists.change-modal-assets': 'assets',
    'product-lists.change-modal-private': 'Private',
    'product-lists.change-modal-public': 'Public',
    'product-lists.change-modal-default': 'Default list',
    'product-lists.change-modal-new': 'NEW LIST',
    'product-lists.change-modal-save': 'SAVE',
    'product-lists.change-modal-close': 'Close modal',
    'product-lists.change-modal-add': 'ADD TO',
  });

  // Fetch user's lists
  const { data: listsData, mutate: refreshLists } =
    useSWR<GetProductListsV2Response>(
      shouldFetch ? '/api/lists' : null,
      failingFetcher
    );

  const lists = useMemo(() => listsData ?? [], [listsData]);

  const defaultList = useMemo(() => {
    if (optimisticFavoriteId) {
      const optimistic = lists.find((list) => list.id === optimisticFavoriteId);
      if (optimistic) return optimistic;
    }
    return lists.find((list) => list.isFavorites) ?? lists[0] ?? null;
  }, [lists, optimisticFavoriteId]);

  const fetchProductMembership = useCallback(
    async (productId: string): Promise<string[]> => {
      if (!shouldFetch) return [];
      try {
        const url = `/api/lists/products/lookup?product-ids=${encodeURIComponent(productId)}`;
        const data = (await failingFetcher(
          url,
          {}
        )) as LookupProductListsResponse;
        const listIds = data.products[productId] ?? [];
        setProductMembership((prev) => ({ ...prev, [productId]: listIds }));
        return listIds;
      } catch (error) {
        console.error('Error fetching product membership:', error);
        return [];
      }
    },
    [shouldFetch]
  );

  const fetchBatchProductMembership = useCallback(
    async (productIds: string[]): Promise<void> => {
      if (!shouldFetch || productIds.length === 0) return;
      try {
        const ids = productIds.map(encodeURIComponent).join(',');
        const url = `/api/lists/products/lookup?product-ids=${ids}`;
        const data = (await failingFetcher(
          url,
          {}
        )) as LookupProductListsResponse;
        setProductMembership((prev) => {
          const next = { ...prev };
          for (const productId of productIds) {
            next[productId] = data.products[productId] ?? [];
          }
          return next;
        });
      } catch (error) {
        console.error('Error fetching batch product membership:', error);
      }
    },
    [shouldFetch]
  );

  const fetchProductMembershipRef = useRef(fetchProductMembership);
  fetchProductMembershipRef.current = fetchProductMembership;

  const productMembershipRef = useRef(productMembership);
  productMembershipRef.current = productMembership;

  const isProductInList = useCallback(
    (productId: string): boolean | null => {
      const membership = productMembership[productId];
      if (membership === undefined) return null;
      return membership.length > 0;
    },
    [productMembership]
  );

  const addToList = useCallback(
    async (productId: string, list: ProductListV2) => {
      const prevMembership = productMembershipRef.current[productId] ?? [];
      setProductMembership((prev) => ({
        ...prev,
        [productId]: [...prevMembership, list.id],
      }));

      try {
        await addItemToList(list.id, productId);

        setSnackbar({
          type: 'added',
          listName: list.name,
          listSlug: list.slug,
          productId,
          key: Date.now(),
        });
        await refreshLists();
      } catch (error) {
        setProductMembership((prev) => ({
          ...prev,
          [productId]: prevMembership,
        }));
        throw error;
      }
    },
    [refreshLists]
  );

  const removeFromList = useCallback(
    async (productId: string, list: ProductListV2) => {
      const prevMembership = productMembershipRef.current[productId] ?? [];
      setProductMembership((prev) => ({
        ...prev,
        [productId]: prevMembership.filter((id) => id !== list.id),
      }));

      try {
        await removeItemFromList(list.id, productId);

        setSnackbar({
          type: 'removed',
          listName: list.name,
          listSlug: list.slug,
          productId,
          key: Date.now(),
        });
        await refreshLists();
      } catch (error) {
        setProductMembership((prev) => ({
          ...prev,
          [productId]: prevMembership,
        }));
        throw error;
      }
    },
    [refreshLists]
  );

  const toggleRef = useRef({
    isAuthenticated,
    defaultList,
    addToList,
    removeFromList,
    lists,
  });
  toggleRef.current = {
    isAuthenticated,
    defaultList,
    addToList,
    removeFromList,
    lists,
  };

  const toggleProductList = useMemo(
    () =>
      debounce(async (productId: string) => {
        const {
          isAuthenticated,
          defaultList,
          addToList,
          removeFromList,
          lists,
        } = toggleRef.current;

        if (!isAuthenticated) {
          signIn();
          return;
        }

        try {
          const listIds =
            productMembershipRef.current[productId] ??
            (await fetchProductMembershipRef.current(productId));

          if (listIds.length > 0 && lists.length === 1) {
            const list = lists.find((l) => l.id === listIds[0]);
            if (list) {
              await removeFromList(productId, list);
            }
          } else if (listIds.length > 0) {
            setModalFlow({
              step: 'change-list',
              productId,
              initialSelectedListIds: listIds,
              intent: 'remove',
              allowDefaultPromotion: true,
            });
          } else {
            if (!defaultList) {
              console.error('No default list available');
              return;
            }
            await addToList(productId, defaultList);
          }
        } catch (error) {
          console.error('Error toggling product in list:', error);
        }
      }, 300),
    []
  );

  const onNewList = useCallback(() => {
    if (!snackbar?.productId) return;
    setModalFlow({
      step: 'create-list',
      productId: snackbar.productId,
      intent: 'add',
      allowDefaultPromotion: true,
    });
  }, [snackbar?.productId]);

  const onChangeList = useCallback(async (productId: string) => {
    const listIds = await fetchProductMembershipRef.current(productId);
    setModalFlow({
      step: 'change-list',
      productId,
      initialSelectedListIds: listIds,
      intent: 'add',
      allowDefaultPromotion: true,
    });
  }, []);

  const handleCreateListSave = useCallback(
    async (data: FavoriteListModalFormData) => {
      if (modalFlow.step !== 'create-list') return;
      const { productId, intent, allowDefaultPromotion } = modalFlow;

      setIsSubmitting(true);
      try {
        const newList = await createListFetcher(data.listName, data.visibility);
        await addItemToList(newList.id, productId);
        setProductMembership((prev) => ({
          ...prev,
          [productId]: [...(prev[productId] ?? []), newList.id],
        }));
        await refreshLists();

        const lookupUrl = `/api/lists/products/lookup?product-ids=${encodeURIComponent(productId)}`;
        const lookupData = (await failingFetcher(
          lookupUrl,
          {}
        )) as LookupProductListsResponse;
        const lookupIds = lookupData.products[productId] ?? [];
        // This is to ensure the new list is selected in the modal,
        // sometimes the backend lookup doesn't return the new list right after creation.
        const initialSelectedListIds = lookupIds.includes(newList.id)
          ? lookupIds
          : [...lookupIds, newList.id];

        setModalFlow({
          step: 'change-list',
          productId,
          initialSelectedListIds,
          fromCreateFlow: true,
          intent,
          allowDefaultPromotion,
        });
      } catch (error) {
        console.error('Error creating list and adding product:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [modalFlow, refreshLists]
  );

  const handleChangeListSave = useCallback(
    async (selectedLists: ListData[]) => {
      if (modalFlow.step !== 'change-list') return;
      const { productId, initialSelectedListIds } = modalFlow;

      const selectedIds = new Set(selectedLists.map((l) => l.id));
      const initialSet = new Set(initialSelectedListIds);

      const listsToAdd = Array.from(selectedIds).filter(
        (id) => !initialSet.has(id)
      );
      const listsToRemove = Array.from(initialSet).filter(
        (id) => !selectedIds.has(id)
      );

      if (listsToAdd.length === 0 && listsToRemove.length === 0) {
        if (modalFlow.fromCreateFlow && selectedLists.length > 0) {
          const findList = (id: string) => lists.find((l) => l.id === id);
          const list =
            selectedLists.length === 1 ? findList(selectedLists[0].id) : null;
          setSnackbar({
            type: 'added',
            listName:
              list?.name ??
              selectedLists[0]?.name ??
              `${selectedLists.length} lists`,
            listSlug: list?.slug ?? '',
            productId,
            key: Date.now(),
          });
        }
        setModalFlow({ step: 'idle' });
        return;
      }

      setIsSubmitting(true);
      try {
        await Promise.all([
          ...listsToAdd.map((listId) => addItemToList(listId, productId)),
          ...listsToRemove.map((listId) =>
            removeItemFromList(listId, productId)
          ),
        ]);

        if (
          modalFlow.allowDefaultPromotion &&
          selectedLists.length === 1 &&
          selectedLists[0].id !== defaultList?.id
        ) {
          const newFavoriteId = selectedLists[0].id;
          setOptimisticFavoriteId(newFavoriteId);
          try {
            await setFavoriteList(newFavoriteId);
          } catch (error) {
            console.error('Error setting favorite list:', error);
            setOptimisticFavoriteId(null);
          }
        }

        await refreshLists();
        setOptimisticFavoriteId(null);

        const finalListIds = selectedLists.map((l) => l.id);
        setProductMembership((prev) => ({
          ...prev,
          [productId]: finalListIds,
        }));

        const { intent } = modalFlow;
        const relevantCount =
          intent === 'remove' ? listsToRemove.length : listsToAdd.length;

        const findList = (id: string) => lists.find((l) => l.id === id);

        if (modalFlow.fromCreateFlow && selectedLists.length > 0) {
          const list =
            selectedLists.length === 1 ? findList(selectedLists[0].id) : null;
          setSnackbar({
            type: 'added',
            listName:
              list?.name ??
              selectedLists[0]?.name ??
              `${selectedLists.length} lists`,
            listSlug: list?.slug ?? '',
            productId,
            key: Date.now(),
          });
        } else if (relevantCount === 0) {
          if (selectedLists.length > 0) {
            const list =
              selectedLists.length === 1 ? findList(selectedLists[0].id) : null;
            setSnackbar({
              type: 'added',
              listName:
                list?.name ??
                selectedLists[0]?.name ??
                `${selectedLists.length} lists`,
              listSlug: list?.slug ?? '',
              productId,
              key: Date.now(),
            });
          } else {
            const list =
              listsToRemove.length === 1 ? findList(listsToRemove[0]) : null;
            setSnackbar({
              type: 'removed',
              listName: list?.name ?? `${listsToRemove.length} lists`,
              listSlug: list?.slug ?? '',
              productId,
              key: Date.now(),
            });
          }
        } else if (intent === 'remove') {
          if (listsToAdd.length > 0) {
            const list =
              listsToAdd.length === 1 ? findList(listsToAdd[0]) : null;
            setSnackbar({
              type: 'added',
              listName: list?.name ?? `${listsToAdd.length} lists`,
              listSlug: list?.slug ?? '',
              productId,
              key: Date.now(),
            });
          } else {
            const list =
              listsToRemove.length === 1 ? findList(listsToRemove[0]) : null;
            setSnackbar({
              type: 'removed',
              listName: list?.name ?? `${listsToRemove.length} lists`,
              listSlug: list?.slug ?? '',
              productId,
              key: Date.now(),
            });
          }
        } else {
          const list = listsToAdd.length === 1 ? findList(listsToAdd[0]) : null;
          setSnackbar({
            type: 'added',
            listName: list?.name ?? `${listsToAdd.length} lists`,
            listSlug: list?.slug ?? '',
            productId,
            key: Date.now(),
          });
        }
      } catch (error) {
        console.error('Error syncing list changes:', error);
      } finally {
        setIsSubmitting(false);
      }

      setModalFlow({ step: 'idle' });
    },
    [modalFlow, refreshLists, lists, defaultList]
  );

  const handleChangeListNewList = useCallback(() => {
    if (modalFlow.step !== 'change-list') return;
    setModalFlow({
      step: 'create-list',
      productId: modalFlow.productId,
      intent: modalFlow.intent,
      allowDefaultPromotion: modalFlow.allowDefaultPromotion,
    });
  }, [modalFlow]);

  const createModalMicrocopy = useMemo<FavoriteListModalPropsMicrocopy>(
    () => ({
      title: mc['product-lists.create-modal-title'],
      subTitle: mc['product-lists.create-modal-subtitle'],
      listNameLabel: mc['product-lists.create-modal-name-label'],
      listNamePlaceholder: mc['product-lists.create-modal-name-placeholder'],
      visibilityLabel: mc['product-lists.create-modal-visibility-label'],
      accountTypeLabel: '',
      visibility: {
        groupLabel: mc['product-lists.visibility-group-label'],
        private: mc['product-lists.visibility-private'],
        privateHelp: mc['product-lists.visibility-private-help'],
        public: mc['product-lists.visibility-public'],
        publicHelp: mc['product-lists.visibility-public-help'],
      },
      accountType: { ariaLabel: '', personal: '', organization: '' },
      cta: mc['product-lists.create-modal-cta'],
      close: mc['product-lists.create-modal-close'],
      clearInput: mc['product-lists.create-modal-clear-input'],
    }),
    [mc]
  );

  const isFromCreateFlow =
    modalFlow.step === 'change-list' && modalFlow.fromCreateFlow;

  const changeModalMicrocopy = useMemo<ChangeFavoriteListModalPropsMicrocopy>(
    () => ({
      title: isFromCreateFlow
        ? mc['product-lists.change-modal-title-from-create']
        : mc['product-lists.change-modal-title'],
      subTitle: isFromCreateFlow
        ? mc['product-lists.change-modal-subtitle-from-create']
        : mc['product-lists.change-modal-subtitle'],
      assets: mc['product-lists.change-modal-assets'],
      private: mc['product-lists.change-modal-private'],
      public: mc['product-lists.change-modal-public'],
      default: mc['product-lists.change-modal-default'],
      new: mc['product-lists.change-modal-new'],
      add: mc['product-lists.change-modal-add'],
      save: mc['product-lists.change-modal-save'],
      close: mc['product-lists.change-modal-close'],
      personal: '',
      organization: '',
      tabsAriaLabel: '',
    }),
    [mc, isFromCreateFlow]
  );

  const handleSnackbarClose = useCallback(
    () =>
      setSnackbar((current) =>
        current?.key === snackbar?.key ? null : current
      ),
    [snackbar?.key]
  );

  const snackbarProps = useMemo(() => {
    if (!snackbar) return null;

    const hasMultipleLists = lists.length > 1;

    if (snackbar.type === 'removed') {
      return {
        iconWeight: 'bold' as const,
        closeIcon: 'X',
        microcopy: {
          title: mc['product-lists.removed-from'],
          actionLabel: snackbar.listName,
          closeAriaLabel: mc['product-lists.close'],
        },
      };
    }

    return {
      iconWeight: 'fill' as const,
      ...(snackbar.listSlug
        ? {
            actionIcon: 'ArrowSquareOut',
            onActionClick: () =>
              window.open(`/lists/${snackbar.listSlug}`, '_blank'),
          }
        : {}),
      closeIcon: hasMultipleLists ? 'CaretDown' : 'Plus',
      microcopy: {
        title: mc['product-lists.added-to'],
        ...(snackbar.listSlug
          ? { actionAriaLabel: mc['product-lists.view-list'] }
          : {}),
        actionLabel: snackbar.listName,
        closeAriaLabel: hasMultipleLists
          ? mc['product-lists.change-list']
          : mc['product-lists.add-to-new-list'],
      },
      onCloseIconClick: hasMultipleLists
        ? () => onChangeList(snackbar.productId)
        : onNewList,
    };
  }, [snackbar, lists.length, mc, onNewList, onChangeList]);

  const value = useMemo(
    () => ({
      lists,
      defaultList,
      isLoading: !listsData && isAuthenticated,
      isProductInList,
      toggleProductList,
      onNewList,
      onChangeList,
      fetchProductMembership,
      fetchBatchProductMembership,
    }),
    [
      lists,
      defaultList,
      listsData,
      isAuthenticated,
      isProductInList,
      toggleProductList,
      onNewList,
      onChangeList,
      fetchProductMembership,
      fetchBatchProductMembership,
    ]
  );

  return (
    <ProductListsContext.Provider value={value}>
      {children}
      {snackbarProps && snackbar && (
        <SnackBar
          key={snackbar.key}
          icon="BookmarkSimple"
          onClose={handleSnackbarClose}
          {...snackbarProps}
        />
      )}
      <FavoriteListModal
        open={modalFlow.step === 'create-list'}
        onOpenChange={(open) => {
          if (!open) setModalFlow({ step: 'idle' });
        }}
        listName=""
        visibility="private"
        accountType="personal"
        showAccountType={false}
        userName={session?.user?.name ?? ''}
        userProfilePicture={session?.user?.image}
        publisherName=""
        microcopy={createModalMicrocopy}
        onSave={handleCreateListSave}
        isSubmitting={isSubmitting}
      />
      {modalFlow.step === 'change-list' && (
        <ChangeFavoriteListModal
          open
          onOpenChange={(open) => {
            if (!open) setModalFlow({ step: 'idle' });
          }}
          list={lists.map((l) => ({
            ...toListData(l),
            ...(optimisticFavoriteId
              ? { default: l.id === optimisticFavoriteId }
              : {}),
          }))}
          multiTabs={false}
          initialSelectedListIds={modalFlow.initialSelectedListIds}
          forceEnableSave={isFromCreateFlow}
          microcopy={changeModalMicrocopy}
          onNewList={handleChangeListNewList}
          onSave={handleChangeListSave}
          isSubmitting={isSubmitting}
        />
      )}
    </ProductListsContext.Provider>
  );
};

export const useProductLists = () => {
  const context = useContext(ProductListsContext);
  return context ?? DEFAULT_CONTEXT;
};
